/**
 * <delivery-picker>
 * -----------------
 * Lets a shopper check delivery serviceability by pincode and pick a
 * delivery option (courier / speed / price) from an external API
 * (default: https://undress-pincode.onrender.com/check/<pincode>).
 *
 * The chosen option is:
 *  - persisted to localStorage so it survives navigation between the
 *    product page, cart page and cart drawer, and
 *  - written to the Shopify cart as cart attributes via /cart/update.js,
 *    so it is carried through to checkout as reference information and
 *    shows up on the order's "Additional details" once placed.
 *
 * Shopify does not let theme code choose the shipping rate a customer
 * pays at checkout — that is always calculated by Shopify from the
 * store's Settings > Shipping and delivery rates. This component's job
 * ends at the cart: it shows the shopper's chosen option clearly through
 * to checkout hand-off. It does not create Shopify orders itself —
 * orders are created by Shopify's own checkout after payment.
 *
 * API response shape:
 * {
 *   "pincode": "110020",
 *   "serviceable": true,
 *   "courier": "DTDC",
 *   "options": [
 *     { "code": "STANDARD", "name": "Standard Delivery", "price": 0,
 *       "transitDays": 2, "transitLabel": "2 business days",
 *       "cod": false, "courier": "DTDC" }
 *   ]
 * }
 */
(function () {
  if (window.customElements.get('delivery-picker')) return;

  var STORAGE_KEY = 'delivery-picker:selection';
  var PINCODE_KEY = 'delivery-picker:last-pincode';
  var EVENT_SELECTED = 'delivery-picker:selected';
  var EVENT_CLEARED = 'delivery-picker:cleared';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function truckIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v10H3z"/><path d="M14 10h4l3 3v4h-7"/><circle cx="7.5" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>';
  }
  function clockIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
  }
  function okIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l2.5 2.5L16 9"/></svg>';
  }
  function noIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>';
  }

  var nf;
  try { nf = new Intl.NumberFormat('en-IN'); } catch (e) { nf = { format: function (n) { return String(n); } }; }

  var cartUpdateUrl = function () {
    return (window.theme && window.theme.routes && window.theme.routes.cartUpdate) || '/cart/update.js';
  };

  var DeliveryPicker = class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.dpBound === '1') return;
      this.dataset.dpBound = '1';

      this.instanceId = 'dp' + Math.random().toString(36).slice(2);
      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.currency = this.dataset.currency || '₹';
      this.freeLabel = this.dataset.freeLabel || 'FREE';
      this.autocheck = this.dataset.autocheck === 'true';

      this.formPanel = this.querySelector('[data-dp-panel="form"]');
      this.summaryPanel = this.querySelector('[data-dp-panel="summary"]');
      this.summaryEl = this.querySelector('[data-dp-summary]');
      this.form = this.querySelector('[data-dp-form]');
      this.input = this.querySelector('[data-dp-input]');
      this.btn = this.querySelector('[data-dp-submit]');
      this.btnLabel = this.querySelector('[data-dp-btn-label]');
      this.msg = this.querySelector('[data-dp-msg]');
      this.result = this.querySelector('[data-dp-result]');
      this.skeleton = this.querySelector('[data-dp-skeleton]');
      this.defaultBtnText = this.btnLabel ? this.btnLabel.textContent : '';

      this.boundOnSelectedElsewhere = this.onSelectedElsewhere.bind(this);
      this.boundOnClearedElsewhere = this.onClearedElsewhere.bind(this);
      document.addEventListener(EVENT_SELECTED, this.boundOnSelectedElsewhere);
      document.addEventListener(EVENT_CLEARED, this.boundOnClearedElsewhere);

      this.bindEvents();

      var selection = this.readStoredSelection() || this.readInitialFromServer();
      if (selection) {
        this.showSummary(selection);
      } else {
        this.showForm();
        if (this.autocheck) {
          var savedPin = this.readStoredPincode();
          if (savedPin) {
            this.input.value = savedPin;
            this.check(savedPin);
          }
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT_SELECTED, this.boundOnSelectedElsewhere);
      document.removeEventListener(EVENT_CLEARED, this.boundOnClearedElsewhere);
    }

    onSelectedElsewhere(evt) {
      if (!evt.detail || evt.detail.originId === this.instanceId) return;
      this.showSummary(evt.detail);
    }

    onClearedElsewhere(evt) {
      if (evt.detail && evt.detail.originId === this.instanceId) return;
      this.showForm();
    }

    readStoredSelection() {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        var parsed = raw ? JSON.parse(raw) : null;
        return parsed && parsed.code ? parsed : null;
      } catch (e) { return null; }
    }

    readStoredPincode() {
      var sel = this.readStoredSelection();
      if (sel && sel.pincode) return sel.pincode;
      try { return localStorage.getItem(PINCODE_KEY); } catch (e) { return null; }
    }

    readInitialFromServer() {
      var d = this.dataset;
      if (!d.initialCode) return null;
      return {
        code: d.initialCode,
        name: d.initialName || d.initialCode,
        price: d.initialPrice !== undefined ? Number(d.initialPrice) : undefined,
        priceLabel: d.initialPriceLabel || '',
        eta: d.initialEta || '',
        courier: d.initialCourier || '',
        cod: d.initialCod === 'true',
        codFee: d.initialCodFee ? Number(d.initialCodFee) : null,
        pincode: d.initialPincode || ''
      };
    }

    persist(selection) {
      try {
        if (selection) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
          localStorage.setItem(PINCODE_KEY, selection.pincode || '');
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) { /* storage unavailable — non-fatal */ }
    }

    bindEvents() {
      var self = this;
      this.input.addEventListener('input', function () {
        self.input.value = self.input.value.replace(/\D/g, '').slice(0, 6);
        if (self.msg.classList.contains('is-visible')) self.clearError();
      });
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.check(self.input.value.trim());
      });
    }

    showForm() {
      if (this.summaryPanel) this.summaryPanel.hidden = true;
      if (this.formPanel) this.formPanel.hidden = false;
    }

    showFormForReselect() {
      this.showForm();
      var pin = (this.readStoredSelection() || {}).pincode || this.input.value;
      if (pin) {
        this.input.value = pin;
        this.check(pin);
      }
    }

    clearError() {
      this.msg.textContent = '';
      this.msg.classList.remove('is-visible');
    }

    showError(text) {
      this.result.className = 'delivery-picker__result';
      this.result.innerHTML = '';
      this.msg.textContent = text;
      this.msg.classList.add('is-visible');
    }

    setLoading(on) {
      this.btn.disabled = on;
      this.input.disabled = on;
      this.btnLabel.innerHTML = on
        ? '<span class="delivery-picker__spinner" aria-hidden="true"></span>'
        : esc(this.defaultBtnText);
      if (this.skeleton) {
        this.skeleton.classList.toggle('is-visible', on);
      }
      if (on) {
        this.result.className = 'delivery-picker__result';
        this.result.innerHTML = '';
      }
    }

    formatPrice(price) {
      if (price === 0 || price === '0' || price == null) {
        return { html: '<span class="delivery-picker__price delivery-picker__price--free">' + esc(this.freeLabel) + '</span>', label: this.freeLabel, isFree: true };
      }
      var label = this.currency + nf.format(price);
      return { html: '<span class="delivery-picker__price">' + esc(label) + '</span>', label: label, isFree: false };
    }

    check(pin) {
      var self = this;
      if (!/^\d{6}$/.test(pin)) {
        this.showError('Please enter a valid 6-digit pincode.');
        return;
      }
      this.clearError();
      this.setLoading(true);
      fetch(this.apiBase + '/check/' + encodeURIComponent(pin), { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json().catch(function () { return null; }).then(function (body) {
            return { ok: res.ok, body: body };
          });
        })
        .then(function (r) {
          if (!r.body || r.body.error) {
            self.showError((r.body && r.body.error) || 'Could not check this pincode right now.');
            return;
          }
          try { localStorage.setItem(PINCODE_KEY, pin); } catch (e) {}
          self.renderOptions(r.body);
        })
        .catch(function () {
          self.showError('Network error. Please check your connection and try again.');
        })
        .then(function () { self.setLoading(false); });
    }

    renderOptions(data) {
      var self = this;
      this.clearError();
      var pin = esc(data.pincode || this.input.value);
      var serviceable = data.serviceable && Array.isArray(data.options) && data.options.length > 0;

      if (!serviceable) {
        this.result.className = 'delivery-picker__result is-visible';
        this.result.innerHTML =
          '<div class="delivery-picker__status delivery-picker__status--no">' +
            '<span class="delivery-picker__status-text">' + noIcon() + 'Sorry, we don’t deliver to ' + pin + ' yet</span>' +
          '</div>' +
          '<button type="button" class="delivery-picker__reset" data-dp-reset>Try another pincode</button>';
        this.bindResultActions();
        return;
      }

      var rows = data.options.map(function (o, i) {
        var eta = o.transitLabel || (o.transitDays ? o.transitDays + ' business days' : '');
        var priceInfo = self.formatPrice(o.price);
        // The option's own name already says "Cash on Delivery" when code is
        // COD - showing the COD chip too would just repeat the same label.
        // Only show it when COD is flagged on a row named something else.
        var showCodChip = o.cod && o.code !== 'COD';
        return (
          '<li>' +
            '<button type="button" class="delivery-picker__option" role="radio" aria-checked="false" data-dp-option data-dp-index="' + i + '">' +
              '<span class="delivery-picker__option-main">' +
                '<span class="delivery-picker__radio" aria-hidden="true"></span>' +
                '<span class="delivery-picker__opt-body">' +
                  '<span class="delivery-picker__opt-name">' + esc(o.name || o.code) +
                    (showCodChip ? '<span class="delivery-picker__cod">COD</span>' : '') +
                  '</span>' +
                  (eta ? '<span class="delivery-picker__opt-eta">' + clockIcon() + 'Delivered in ' + esc(eta) + '</span>' : '') +
                  (o.codFee ? '<span class="delivery-picker__opt-fee-note">+ ' + esc(self.currency + o.codFee) + ' COD handling fee, added separately at checkout</span>' : '') +
                '</span>' +
              '</span>' +
              priceInfo.html +
            '</button>' +
          '</li>'
        );
      }).join('');

      this.result.className = 'delivery-picker__result is-visible';
      this.result.innerHTML =
        '<div class="delivery-picker__status delivery-picker__status--ok">' +
          '<span class="delivery-picker__status-text">' + okIcon() + 'Delivery available to ' + pin + '</span>' +
          (data.courier ? '<span class="delivery-picker__chip">via ' + esc(data.courier) + '</span>' : '') +
        '</div>' +
        '<ul class="delivery-picker__options" role="radiogroup" aria-label="Delivery options">' + rows + '</ul>' +
        '<p class="delivery-picker__hint">Select an option to continue.</p>' +
        '<button type="button" class="delivery-picker__reset" data-dp-reset>Change pincode</button>';

      this.result.querySelectorAll('[data-dp-option]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.result.querySelectorAll('[data-dp-option]').forEach(function (opt) {
            opt.setAttribute('aria-checked', String(opt === el));
          });
          var idx = parseInt(el.dataset.dpIndex, 10);
          self.selectOption(data.options[idx], data.pincode || self.input.value, data.courier);
        });
      });
      this.bindResultActions();
    }

    bindResultActions() {
      var self = this;
      var r = this.result.querySelector('[data-dp-reset]');
      if (r) {
        r.addEventListener('click', function () {
          self.result.className = 'delivery-picker__result';
          self.result.innerHTML = '';
          self.input.value = '';
          self.input.focus();
        });
      }
    }

    selectOption(option, pincode, dataCourier) {
      var priceInfo = this.formatPrice(option.price);
      var selection = {
        originId: this.instanceId,
        pincode: pincode,
        code: option.code,
        name: option.name || option.code,
        price: option.price,
        priceLabel: priceInfo.label,
        eta: option.transitLabel || (option.transitDays ? option.transitDays + ' business days' : ''),
        courier: option.courier || dataCourier || '',
        cod: !!option.cod,
        codFee: option.codFee || null
      };

      this.persist(selection);
      this.applyToCart(selection);
      this.showSummary(selection, { justSelected: true });
      document.dispatchEvent(new CustomEvent(EVENT_SELECTED, { detail: selection, bubbles: false }));
    }

    applyToCart(selection) {
      fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Delivery Option': selection.name,
            'Delivery Code': selection.code,
            'Delivery Price': selection.priceLabel,
            'Delivery ETA': selection.eta || '',
            'Delivery Courier': selection.courier || '',
            'Delivery Pincode': selection.pincode || '',
            'Delivery COD': selection.cod ? 'Yes' : '',
            'Delivery COD Fee': selection.codFee ? String(selection.codFee) : ''
          }
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart update failed');
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        })
        .catch(function () {
          // Selection still stands locally (localStorage + on-page summary); the
          // cart attribute resyncs on the next successful cart-changing action.
        });
    }

    clearSelection() {
      var self = this;
      this.persist(null);
      fetch(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Delivery Option': '',
            'Delivery Code': '',
            'Delivery Price': '',
            'Delivery ETA': '',
            'Delivery Courier': '',
            'Delivery Pincode': '',
            'Delivery COD': '',
            'Delivery COD Fee': ''
          }
        })
      })
        .then(function () {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        })
        .catch(function () {});
      document.dispatchEvent(new CustomEvent(EVENT_CLEARED, { detail: { originId: self.instanceId }, bubbles: false }));
      this.showFormForReselect();
    }

    showSummary(selection, opts) {
      var self = this;
      if (this.formPanel) this.formPanel.hidden = true;
      if (this.summaryPanel) this.summaryPanel.hidden = false;
      if (!this.summaryEl) return;

      var isFree = selection.price === 0 || selection.price === '0' || selection.priceLabel === this.freeLabel;
      // The name already reads "Cash on Delivery" when code is COD - the chip
      // would just repeat it, so only show it when COD rides on another name.
      var showCodChip = selection.cod && selection.code !== 'COD';
      this.summaryEl.innerHTML =
        '<div class="delivery-picker__summary">' +
          '<span class="delivery-picker__summary-main">' +
            '<span class="delivery-picker__summary-icon" aria-hidden="true">' + truckIcon() + '</span>' +
            '<span>' +
              '<span class="delivery-picker__summary-label">Delivery' + (selection.pincode ? ' to ' + esc(selection.pincode) : '') + '</span>' +
              '<span class="delivery-picker__summary-name">' + esc(selection.name) +
                (showCodChip ? '<span class="delivery-picker__cod">COD</span>' : '') +
              '</span>' +
              (selection.eta || selection.courier ? (
                '<span class="delivery-picker__summary-meta">' + clockIcon() +
                  (selection.eta ? esc(selection.eta) : '') +
                  (selection.courier ? (selection.eta ? ' · ' : '') + 'via ' + esc(selection.courier) : '') +
                '</span>'
              ) : '') +
              (selection.codFee ? '<span class="delivery-picker__opt-fee-note">+ ' + esc(this.currency + selection.codFee) + ' COD handling fee, added separately at checkout</span>' : '') +
            '</span>' +
          '</span>' +
          '<span class="delivery-picker__summary-side">' +
            '<span class="delivery-picker__summary-price' + (isFree ? ' delivery-picker__summary-price--free' : '') + '">' + esc(selection.priceLabel) + '</span>' +
            '<button type="button" class="delivery-picker__summary-change" data-dp-change>Change</button>' +
          '</span>' +
        '</div>';

      var changeBtn = this.summaryEl.querySelector('[data-dp-change]');
      if (changeBtn) {
        changeBtn.addEventListener('click', function () {
          self.showFormForReselect();
        });
      }

      if (opts && opts.justSelected) {
        this.summaryEl.classList.add('delivery-picker__summary-flash');
        setTimeout(function () {
          if (self.summaryEl) self.summaryEl.classList.remove('delivery-picker__summary-flash');
        }, 600);
      }
    }
  };

  window.customElements.define('delivery-picker', DeliveryPicker);
})();
 